/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlaskit/design-system/use-primitives-text */
import { Fragment, useCallback, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import LogOutIcon from '@atlaskit/icon/core/log-out';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import PersonIcon from '@atlaskit/icon/core/person';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getConfig } from '../../../config';
import { CollectFeedbackV2 } from '../CollectFeedback';

import { messages } from './messages';

type UserAvatarDropdownProps = {
	user: {
		accountId: string;
		name: string;
		picture: string;
	};
	/**
	 * When true, renders the dropdown menu in its open state on initial mount.
	 * Intended for Storybook / visual regression snapshots that want to
	 * exercise the menu contents deterministically — production callers
	 * should leave this unset and rely on user interaction.
	 */
	defaultOpen?: boolean;
};

const styles = cssMap({
	userMenuTrigger: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 0,
		border: 'none',
		backgroundColor: 'transparent',
		cursor: 'pointer',
		borderRadius: token('radius.full'),
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	userPicture: {
		width: '32px',
		height: '32px',
		borderRadius: token('radius.full'),
		objectFit: 'cover',
		display: 'block',
	},
	dropdownItem: {
		width: '200px',
	},
	mobileFeedbackMenuItem: {
		display: 'none',
		'@media (max-width: 30rem)': {
			display: 'block',
		},
	},
});

export const UserAvatarDropdown = ({ user, defaultOpen }: UserAvatarDropdownProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

	const openFeedback = useCallback(() => setIsFeedbackOpen(true), []);
	const closeFeedback = useCallback(() => setIsFeedbackOpen(false), []);

	return (
		<Fragment>
			<DropdownMenu<HTMLButtonElement>
				defaultOpen={defaultOpen}
				trigger={({ triggerRef, ...triggerProps }) => (
					<button
						{...triggerProps}
						ref={triggerRef}
						type="button"
						css={styles.userMenuTrigger}
						aria-label={formatMessage(messages.userAccountMenuAriaLabel, {
							userName: user.name,
						})}
					>
						<img src={user.picture} alt="" css={styles.userPicture} />
					</button>
				)}
				shouldRenderToParent
				placement="bottom-end"
			>
				<DropdownItemGroup>
					<DropdownItem
						onClick={() =>
							window.open(
								`${getConfig().atlassianHomeUrl}/people/${encodeURIComponent(user.accountId)}`,
								'_blank',
							)
						}
					>
						<Inline space="space.150" alignBlock="center" xcss={styles.dropdownItem}>
							<PersonIcon label={formatMessage(messages.profileLabel)} />
							<Text>
								<FormattedMessage {...messages.profileLabel} />
							</Text>
						</Inline>
					</DropdownItem>
				</DropdownItemGroup>
				<DropdownItemGroup hasSeparator>
					<div css={styles.mobileFeedbackMenuItem}>
						<DropdownItem onClick={openFeedback}>
							<Inline space="space.150" alignBlock="center" xcss={styles.dropdownItem}>
								<MegaphoneIcon label={formatMessage(messages.sendFeedback)} />
								<Text>
									<FormattedMessage {...messages.sendFeedback} />
								</Text>
							</Inline>
						</DropdownItem>
					</div>
					<DropdownItem
						onClick={() => {
							globalThis.window.location.replace(
								`${getConfig().atlassianAccountUrl}/logout?application=twg-microsite-bifrost&continue=${encodeURIComponent(
									getConfig().serviceUrl,
								)}`,
							);
						}}
					>
						<Inline space="space.150" alignBlock="center" xcss={styles.dropdownItem}>
							<LogOutIcon label={formatMessage(messages.logoutLabel)} />
							<Text>
								<FormattedMessage {...messages.logoutLabel} />
							</Text>
						</Inline>
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>

			<CollectFeedbackV2
				atlassianAccountId={user.accountId}
				name={user.name}
				isOpen={isFeedbackOpen}
				onClose={closeFeedback}
			/>
		</Fragment>
	);
};
